import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Check, Sparkles, ArrowRight } from "lucide-react";

const TIERS = [
  {
    key: "starter",
    name: "Starter",
    tagline: "For small teams running their first real plans.",
    monthly: 14, annual: 11, minSeats: 3,
    codes: { monthly: "starter_monthly", annual: "starter_annual" },
    features: [
      "Minimum 3 seats · up to 5 team members",
      "3 active projects",
      "Gantt, WBS & critical path with baselines",
      "Kanban board with swimlanes",
      "RAID log, actions & decisions",
      "Requirements (RTM) with PDF export",
      "Status reports & meeting minutes",
      "Email support",
    ],
  },
  {
    key: "pro",
    name: "Professional",
    tagline: "For delivery teams who want AI on their side.",
    monthly: 29, annual: 24, minSeats: 5,
    popular: true,
    codes: { monthly: "pro_monthly", annual: "pro_annual" },
    features: [
      "Everything in Starter",
      "Minimum 5 seats · up to 25 team members",
      "25 projects",
      "AI Copilot — charters, status reports, risk suggestions",
      "All four toolkits: PMBOK, Agile, Lean, Six Sigma",
      "Portfolio dashboard & cross-project rollup",
      "Resource workload & capacity planning",
      "Change management, FMEA & Gauge R&R",
      "Excel & MS Project imports",
    ],
  },
  {
    key: "enterprise",
    name: "Enterprise",
    tagline: "For organisations running delivery at scale.",
    monthly: 55, annual: 44, minSeats: 10,
    codes: { monthly: "enterprise_monthly", annual: "enterprise_annual" },
    features: [
      "Everything in Professional",
      "Minimum 10 seats · unlimited team members",
      "Unlimited projects",
      "Daily escalation email digests",
      "Team status consolidation for PM & Sponsor",
      "Portfolio PDF exports for executive reporting",
      "Strict tenant isolation & audit trail",
      "Priority support & guided onboarding",
      "Invoice billing available",
    ],
  },
];

const ADDONS = [
  { name: "AI usage packs", text: "Extra AI Copilot capacity for heavy drafting months — charters, reports and risk analysis on demand." },
  { name: "Guided onboarding & data migration", text: "We move your existing plans in — Excel, MS Project or other tools — and set up your first portfolio." },
  { name: "Dedicated success manager", text: "A named expert for quarterly delivery reviews, methodology coaching and rollout support." },
];

export default function PricingPage() {
  const { user } = useAuth();
  const [interval, setInterval] = useState("monthly");
  const annual = interval === "annual";

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="pricing-page">
      <header className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/logo-icon.png" alt="Ganttastic" className="h-10 w-10" />
          <span className="font-display font-black tracking-tighter text-xl">Ganttastic<span className="text-[#5B93F0]">.app</span></span>
        </Link>
        <nav className="flex items-center gap-3">
          {user && user !== false ? (
            <Link to="/projects"><Button data-testid="goto-app-btn" className="rounded-none bg-[#1E6FE8] hover:bg-[#1557C6]">Go to app</Button></Link>
          ) : (
            <>
              <Link data-testid="pricing-login-link" to="/login" className="text-sm text-neutral-300 hover:text-white underline">Log in</Link>
              <Link to="/signup"><Button data-testid="pricing-signup-btn" className="rounded-none bg-[#1E6FE8] hover:bg-[#1557C6]">Create account</Button></Link>
            </>
          )}
        </nav>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-14 pb-10">
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none">Simple pricing.<br /><span className="text-[#5B93F0]">Serious delivery.</span></h1>
        <p className="mt-5 text-neutral-400 max-w-xl text-base leading-relaxed">
          Every plan starts with a free 14-day trial of everything — no card required. Pick a plan when you're ready.
        </p>
        <div className="mt-8 inline-flex border border-neutral-700" role="group">
          <button data-testid="pricing-toggle-monthly" onClick={() => setInterval("monthly")}
            className={`px-5 py-2 text-sm font-semibold ${!annual ? "bg-[#1E6FE8] text-white" : "text-neutral-400 hover:text-white"}`}>Monthly</button>
          <button data-testid="pricing-toggle-annual" onClick={() => setInterval("annual")}
            className={`px-5 py-2 text-sm font-semibold ${annual ? "bg-[#1E6FE8] text-white" : "text-neutral-400 hover:text-white"}`}>
            Annual <span className={annual ? "text-blue-100" : "text-[#5B93F0]"}>· save ~20%</span>
          </button>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-16 grid md:grid-cols-3 gap-4 items-stretch">
        {TIERS.map((t) => (
          <div key={t.key} data-testid={`pricing-tier-${t.key}`}
            className={`flex flex-col border p-6 ${t.popular ? "border-[#1E6FE8] bg-[#1E6FE8]/5" : "border-neutral-800 bg-neutral-900/50"}`}>
            {t.popular && (
              <div className="inline-flex items-center gap-1.5 self-start mb-3 bg-[#1E6FE8] text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1">
                <Sparkles className="h-3 w-3" /> Most popular
              </div>
            )}
            <div className="overline-label text-neutral-500">{t.name}</div>
            <div className="mt-2 flex items-baseline gap-1">
              <span data-testid={`pricing-${t.key}-price`} className="font-display font-black text-5xl tracking-tighter">${annual ? t.annual : t.monthly}</span>
              <span className="text-sm text-neutral-500">/user/mo</span>
            </div>
            <div className="text-xs text-neutral-500 mt-1">{annual ? `Billed annually ($${t.annual * 12}/yr per seat)` : "Billed monthly"} · minimum {t.minSeats} seats</div>
            <p className="text-sm text-neutral-400 mt-3">{t.tagline}</p>
            <ul className="mt-5 space-y-2.5 flex-1">
              {t.features.map((f) => (
                <li key={f} className="flex gap-2 text-sm text-neutral-300">
                  <Check className="h-4 w-4 text-[#5B93F0] shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
            <Link to={`/signup?plan=${t.codes[annual ? "annual" : "monthly"]}`} className="mt-6">
              <Button data-testid={`pricing-${t.key}-cta`}
                className={`rounded-none w-full ${t.popular ? "bg-[#1E6FE8] hover:bg-[#1557C6]" : "bg-neutral-100 text-neutral-900 hover:bg-white"}`}>
                Start with {t.name} <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </Link>
          </div>
        ))}
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="overline-label text-neutral-500 mb-4">Add-ons — available on any plan</div>
        <div className="grid sm:grid-cols-3 gap-4">
          {ADDONS.map((a) => (
            <div key={a.name} className="border border-neutral-800 bg-neutral-900/50 p-5" data-testid="pricing-addon">
              <h3 className="font-display font-bold tracking-tight">{a.name}</h3>
              <p className="text-sm text-neutral-400 mt-1.5 leading-relaxed">{a.text}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-neutral-600 mt-6">All prices in USD. Cancel any time from the billing page. Enterprise invoice billing — contact us after signup.</p>
      </section>

      <footer className="border-t border-neutral-800 py-6 text-center text-xs text-neutral-600">ganttastic.app · Plan. Align. Deliver.</footer>
    </div>
  );
}
